/**
 * @copyright Copyright 2021-2024 Epic Systems Corporation
 * @file homegrown apps store updater methods
 * @module Epic.AppOrchard.StoreMethods.HomegrownApps
 */

import { IDispatch } from "@epic/react-redux-booster";
import { homegrownAppsActions } from "ao/state/widgets/homegrownApps";
import { CustomerAppChangeRequestType, IHomegrownApp } from "ao/types";

/**
 * Update store with apps after loading from server
 * @param dispatch for useDispatch
 * @param orgId customer org id
 * @param apps apps for customer org
 * @param orgCurrentEpicVersion current Epic version for the org
 */
export function updateAppsAfterLoad(
	dispatch: IDispatch,
	orgId: number,
	apps: IHomegrownApp[],
	orgCurrentEpicVersion: number | null,
	isLocalJKUWarningEnabled: boolean,
) {
	dispatch(
		homegrownAppsActions.updateAppsAfterLoad({
			orgId,
			apps,
			orgCurrentEpicVersion,
			isLocalJKUWarningEnabled,
		}),
	);
}

/**
 * Mark app as internal
 * @param dispatch for useDispatch
 * @param orgId customer org id
 * @param appId customer app ID
 */
export function markAsInternal(dispatch: IDispatch, appId: number, orgId: number) {
	dispatch(homegrownAppsActions.markAsInternal({ appId, orgId }));
}

/**
 * Update (potentially deleting) change request after saving updates to server
 * @param dispatch for useDispatch
 * @param orgId customer org id
 * @param appId customer app ID
 */
export function updateChangeRequest(
	dispatch: IDispatch,
	appId: number,
	orgId: number,
	changeType: CustomerAppChangeRequestType | null,
	comment: string | null,
) {
	dispatch(homegrownAppsActions.updateChangeRequest({ appId, orgId, changeType, comment }));
}
